import { CoralRequest, CoralResponse } from '@amzn/ihm-js-coral-client'

/**
 * Executes HTTP request and returns CoralResponse.
 * @param request - The Coral request to execute
 * @param signal - Optional AbortSignal to cancel the request
 */

import { getLocalDevConfig } from '@src/config/localDevConfig'

/**
 * Local dev proxy — redirects gateway requests to a custom URL.
 * Controlled by src/config/local.dev.config.ts (gitignored).
 * If no config or no gatewayProxyUrl, requests go to the real endpoint.
 */
function resolveUrl (url: string): string {
    const { gatewayProxyUrl } = getLocalDevConfig()
    if (!gatewayProxyUrl) return url
    try {
        const parsed = new URL(url)
        const proxy = new URL(gatewayProxyUrl)
        return `${proxy.origin}${parsed.pathname}${parsed.search}`
    } catch {
        return url
    }
}

// Optional callback invoked whenever the executor observes a 403 response.
// Used by the platform to detect mid-session auth revocation (P465134335)
// and trigger a fresh getCurrentUser fetch. Best-effort — invocation failures
// are logged but never propagate to the caller. Only one handler is
// supported; subsequent registrations replace it. Pass null to unregister.
let onAuthDeniedHandler: (() => void) | null = null

export function registerAuthDeniedHandler (handler: (() => void) | null): void {
    onAuthDeniedHandler = handler
}

export async function executeRequest (request: CoralRequest, signal?: AbortSignal): Promise<CoralResponse<any>> {
    // Stringify body if it's an object to prevent [object Object] in payload
    const requestBody = request.body == null ? request.body : (typeof request.body === 'string' ? request.body : JSON.stringify(request.body))

    const response = await fetch(resolveUrl(request.url), {
        method: request.method,
        headers: request.getHeaderBag(),
        body: requestBody,
        signal,
    })

    let body
    // Handle 204 No Content responses
    if (response.status === 204) {
        body = null
    } else {
        // Check content-type to determine how to parse response
        const contentType = response.headers.get('content-type')
        try {
            if (contentType?.includes('application/json')) {
                body = await response.json()
            } else {
                body = await response.text()
            }
        } catch (error) {
            console.error('[SMP] Failed to parse response:', error)
            body = null
        }
    }

    const headers: Array<[string, string]> = []
    response.headers.forEach((value, key) => {
        headers.push([key, value])
    })

    // Fire the auth-denied handler on 403 — session may have been revoked
    // mid-session. Best-effort: don't block or throw. The handler is
    // expected to trigger a getCurrentUser refresh; the response continues
    // to flow up to the caller unchanged.
    if (response.status === 403 && onAuthDeniedHandler) {
        try {
            onAuthDeniedHandler()
        } catch (err) {
            console.warn('[CoralRequestExecutor] Auth-denied handler threw', err)
        }
    }

    return new CoralResponse({
        headers,
        body,
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
    })
}
